import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/axios";
import { useEffect, useState } from "react";
import { DollarSign, Euro } from "lucide-react";
import Type2 from "../assets/svgs/type2.svg";
import Type1 from "../assets/svgs/type1.svg";
import ComboCss from "../assets/svgs/combo-ccs.svg";
import Chademo from "../assets/svgs/chademo.svg";
import Domestic from "../assets/svgs/domestic-ue.svg";
import Type1CCS from "../assets/svgs/type1-ccs.svg";
import Type3C from "../assets/svgs/type3c.svg";
import { useTranslation } from "react-i18next";
import { getStationInfo } from "../api/station";
import { CONSTANTS } from "../constants/constants";
import TermsSwitch from "../components/TermSwitch";

type ResponseType = { data: { sessionId: string; url: string } };

type ConnectorResponseType = {
  type: string;
  connectorId: string | number;
  power: number;
  status: boolean;
  paymentId: string;
};

export enum ConnectorType {
  TYPE_2 = "T2",
  COMBO_CCS = "CCS",
  CHADEMO = "C",
  TYPE_1 = "T1",
  TYPE_3C = "T3C",
  TYPE_1_CCS = "T1CCS",
  DOMESTIC = "D",
  UNKNOWN = "U",
}

const renderIcon = (type: string) => {
  let src;
  switch (type) {
    case ConnectorType.TYPE_2:
      src = Type2;
      break;
    case ConnectorType.TYPE_1:
      src = Type1;
      break;
    case ConnectorType.COMBO_CCS:
      src = ComboCss;
      break;
    case ConnectorType.CHADEMO:
      src = Chademo;
      break;
    case ConnectorType.DOMESTIC:
      src = Domestic;
      break;
    case ConnectorType.TYPE_1_CCS:
      src = Type1CCS;
      break;
    case ConnectorType.TYPE_3C:
      src = Type3C;
      break;
    default:
      return <></>;
  }
  return <img alt="type" src={src} className="w-8 h-8" />;
};

const StatusCheck = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const { stationId } = useParams();
  const [acceped, setAccepted] = useState(false);
  const [connector, setConnector] = useState<ConnectorResponseType>({
    type: "",
    connectorId: "",
    power: 0,
    status: false,
    paymentId: "",
  });
  const [priceAndCurrency, setPriceAndCurrency] = useState({
    price: null,
    currency: "USD",
  });

  const {
    data: stationData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["station-info", stationId],
    queryFn: () => getStationInfo(stationId as string),

    enabled: !!stationId,
  });

  useEffect(() => {
    if (!isLoading && stationData) {
      const connectors = stationData?.connectors;
      if (connectors.length > 0) {
        setConnector({
          ...connectors[0],
          status: connectors[0].status === "Available",
          paymentId: stationData.paymentId,
        });
        setPriceAndCurrency({
          price: stationData.pricePerKWh,
          currency: stationData.currency,
        });
        if (connectors[0].status !== "Available") {
          Object.values(CONSTANTS.LOCAL_STORAGE_KEYS).forEach((key) =>
            localStorage.removeItem(key)
          );
        }
      }
    }
  }, [stationData]);

  const handleTermsChange = (checked: boolean) => {
    setAccepted(checked);
    localStorage.setItem("accepted", checked ? "1" : "0");
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="h-[48px] w-[300px] rounded-2xl bg-gray-200 animate-pulse"></div>
      );
    }

    if (isError) {
      return (
        <div className="h-[48px] w-full bg-red-600 flex jusitfy-center items-center rounded-2xl">
          <p className="font-semibold text-white text-center w-full">
            {t("status.notFound")}
          </p>
        </div>
      );
    }

    return (
      <div className="flex justify-start relative items-start w-full flex-col gap-6">
        <div
          className={`${
            connector.status ? "bg-green-600" : "bg-red-600"
          } py-4 w-full grid grid-cols-5 gap-1 rounded-2xl px-1`}
        >
          <div className="flex flex-col justify-start items-center text-white font-semibold text-center">
            <div className="flex justify-center text-white text-xs font-semibold items-center w-8 h-8 border border-white rounded-full">
              {connector.connectorId}
            </div>
            <p className="text-white font-semibold text-xs">
              {connector.status ? t("common.available") : t("common.occupied")}
            </p>
          </div>
          <div className="flex flex-col justify-start items-center text-white font-semibold">
            {renderIcon(connector?.type || "")}
            <p className="text-xs">{connector?.type || ""}</p>
          </div>
          <div className="flex flex-col justify-start items-center col-span-1 text-white h- font-semibold">
            <p className="text-xl h-8 text-center">
              {Math.round(connector.power / 1000)}
            </p>
            <p className="text-xs text-center">{t("status.power")}</p>
          </div>
          <div className="flex flex-col justify-start items-center col-span-2 text-white font-semibold">
            <p className="h-8">{t("status.price")}</p>
            <p className="flex justify-center items-center text-xs">
              {priceAndCurrency.currency === "USD" ? (
                <DollarSign className="text-black-600" size={"16px"} />
              ) : (
                <Euro className="text-black-600" size={"16px"} />
              )}
              {priceAndCurrency.price} {t("status.perkWh")}
            </p>
          </div>
        </div>
        <TermsSwitch onChange={handleTermsChange} />
      </div>
    );
  };

  return (
    <>
      <div className="h-screen flex flex-col w-full justify-center items-center gap-4">
        <div className="w-full flex justify-center items-center ">
          {renderContent()}
        </div>
      </div>
      <div className="flex w-full left-0 px-4 items-center justify-center absolute bottom-0 h-[100px]">
        {isLoading ? (
          <div className="p-2 bottom-4 h-[44px] rounded-2xl animate-pulse w-full max-w-[200px] bg-green-400 hover:bg-green-700 text-white text-center cursor-pointer" />
        ) : (
          <button
            className={`p-2 bottom-4 rounded-2xl w-full max-w-[200px] ${
              connector.status
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"
            } ${
              !connector.status ? "" : acceped ? "" : "!bg-gray-300"
            } text-white text-center cursor-pointer`}
            onClick={async () => {
              if (isError) navigate(`/${lang}/scanner`);
              if (connector.status && !acceped) return;

              const response: ResponseType = await api.post(
                "/create-checkout-session",
                {
                  paymentId: connector.paymentId,
                  lang: lang,
                  stationId: stationId,
                }
              );
              localStorage.setItem("stationId", stationId as string);
              localStorage.setItem(
                "connectorId",
                connector.connectorId as string
              );
              localStorage.setItem("sessionId", response.data.sessionId);
              window.location.href = response.data.url;
            }}
          >
            {connector.status ? t("status.payment") : t("status.scanAgain")}
          </button>
        )}
      </div>
    </>
  );
};

export default StatusCheck;
